import { LibraryBig, LoaderCircle, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { BookExportSettings } from "@/features/book-assembly/book-assembly.types";

type BookExportBookMetadataFormProps = {
  defaultAuthorName: string;
  defaultBookTitle: string;
  onSave: () => void;
  onSettingsChange: (patch: Partial<BookExportSettings>) => void;
  saveStatus: "error" | "idle" | "ready" | "saving";
  settings: BookExportSettings;
};

function TextareaField(props: {
  id: string;
  label: string;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={props.id}>{props.label}</Label>
      <textarea
        className="min-h-[112px] w-full rounded-[14px] border border-input bg-white/85 px-4 py-3 text-sm text-foreground shadow-sm outline-none ring-offset-background transition-colors placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/60"
        id={props.id}
        onChange={(event) => props.onChange(event.currentTarget.value)}
        placeholder={props.placeholder}
        value={props.value}
      />
    </div>
  );
}

export function BookExportBookMetadataForm({
  defaultAuthorName,
  defaultBookTitle,
  onSave,
  onSettingsChange,
  saveStatus,
  settings,
}: BookExportBookMetadataFormProps) {
  const { t } = useTranslation();
  const saving = saveStatus === "saving";

  return (
    <section className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-clay">
                {t("bookAssembly.metadata.eyebrow")}
              </p>
              <h3 className="text-2xl text-foreground">{t("bookAssembly.metadata.title")}</h3>
              <p className="text-sm leading-7 text-muted-foreground">
                {t("bookAssembly.metadata.body")}
              </p>
            </div>
            <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground sm:flex">
              <LibraryBig className="h-5 w-5" />
            </div>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="book-title">{t("bookAssembly.metadata.bookTitle")}</Label>
            <Input
              id="book-title"
              onChange={(event) => onSettingsChange({ bookTitle: event.currentTarget.value })}
              placeholder={defaultBookTitle || t("bookAssembly.metadata.bookTitlePlaceholder")}
              value={settings.bookTitle}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="book-author">{t("bookAssembly.metadata.authorName")}</Label>
            <Input
              id="book-author"
              onChange={(event) => onSettingsChange({ authorName: event.currentTarget.value })}
              placeholder={defaultAuthorName || t("bookAssembly.metadata.authorFallback")}
              value={settings.authorName}
            />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="book-subtitle">{t("bookAssembly.metadata.bookSubtitle")}</Label>
            <Input
              id="book-subtitle"
              onChange={(event) => onSettingsChange({ bookSubtitle: event.currentTarget.value })}
              placeholder={t("bookAssembly.metadata.bookSubtitlePlaceholder")}
              value={settings.bookSubtitle}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="book-series">{t("bookAssembly.metadata.seriesName")}</Label>
            <Input
              id="book-series"
              onChange={(event) => onSettingsChange({ seriesName: event.currentTarget.value })}
              placeholder={t("bookAssembly.metadata.seriesNamePlaceholder")}
              value={settings.seriesName}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="book-volume">{t("bookAssembly.metadata.volumeLabel")}</Label>
            <Input
              id="book-volume"
              onChange={(event) => onSettingsChange({ volumeLabel: event.currentTarget.value })}
              placeholder={t("bookAssembly.metadata.volumeLabelPlaceholder")}
              value={settings.volumeLabel}
            />
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <TextareaField
            id="book-author-note"
            label={t("bookAssembly.metadata.authorNote")}
            onChange={(authorNote) => onSettingsChange({ authorNote })}
            placeholder={t("bookAssembly.metadata.authorNotePlaceholder")}
            value={settings.authorNote}
          />
          <TextareaField
            id="book-dedication"
            label={t("bookAssembly.metadata.dedication")}
            onChange={(dedication) => onSettingsChange({ dedication })}
            placeholder={t("bookAssembly.metadata.dedicationPlaceholder")}
            value={settings.dedication}
          />
          <TextareaField
            id="book-special-thanks"
            label={t("bookAssembly.metadata.specialThanks")}
            onChange={(specialThanks) => onSettingsChange({ specialThanks })}
            placeholder={t("bookAssembly.metadata.specialThanksPlaceholder")}
            value={settings.specialThanks}
          />
        </div>

        <div className="flex flex-col gap-3 border-t border-ink/10 pt-5 lg:flex-row lg:items-center lg:justify-between">
          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            {t("bookAssembly.metadata.saveHint")}
          </p>
          <Button disabled={saving} onClick={onSave} type="button" variant="outline">
            {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving
              ? t("bookAssembly.metadata.saving")
              : t("bookAssembly.metadata.save")}
          </Button>
        </div>
    </section>
  );
}
